import { Flex, Text, View, TextField, ActionButton } from '@adobe/react-spectrum';
import * as React from 'react';
import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { AtollonButton, AtollonButtonDesignType } from '../../../ui/Button/AtollonButton';
import Close from '@spectrum-icons/workflow/Close';
import styles from './ColumnsSelector.module.css';
import { ColumnsList, IColumnSection, IColumnItem } from './ColumnsList';

export enum ColumnsSelectorDesignType {
  DEFAULT = 'default',
  NEGATIVE = 'negative',
  POSITIVE = 'positive'
}
export interface IColumnsSelectorProps {
  height?: string | number;
  width?: string | number;
  designType?: ColumnsSelectorDesignType;
  headerTitle?: string;
  selectAllText?: React.ReactNode;
  selectAllButtonDesignType?: AtollonButtonDesignType;
  onSelectAllPress?: () => void;
  onItemSelectionChanged?: (state: boolean, item: IColumnItem) => void;
  onVisibilityChange?: (itemId: string, isVisible: boolean) => void;
  onFilterChange?: (itemId: string, isFilter: boolean) => void;
  onReOrder?: (reorderedItems: IColumnItem[]) => void;
  /** Array of column sections to display. If not provided, default sections will be used. */
  sections?: IColumnSection[];
  isGroup?: boolean;
  isAllowDnD?: boolean;
}

export interface IColumnsSelectorRef {
  getSelectedItems: () => string[];
  clearSelection: () => void;
}

const defaultSections: IColumnSection[] = []

export const ColumnsSelector = forwardRef<IColumnsSelectorRef, IColumnsSelectorProps>((props, forwardedRef) => {
  const { 
    height,
    width,
    designType = ColumnsSelectorDesignType.DEFAULT,
    headerTitle = 'All Columns',
    selectAllText = 'Select All',
    selectAllButtonDesignType = AtollonButtonDesignType.LINK,
    onSelectAllPress,
    onItemSelectionChanged,
    onVisibilityChange,
    onFilterChange,
    onReOrder,
    sections: propSections,
    isGroup = false,
    isAllowDnD = true
  } = props;
  
  var className = styles.columns_selector;
  var classNameHeader = styles.header_section;
  var classNameBody = styles.scrollableContainer;
  var classNameSearchField = styles.search_field;

  if (designType) {
    className += ` ${styles[designType]}`;
    classNameHeader += ` ${styles[designType+'-header']}`;
    classNameBody += ` ${styles[designType+'-body']}`;
    classNameSearchField += ` ${styles[designType+'-search-field']}`;
  }
  
  // Initialize sections state with propSections or defaultSections
  const [sections, setSections] = React.useState<IColumnSection[]>(
    propSections !== undefined ? propSections : defaultSections
  );

  // Sync state when propSections changes (controlled component pattern)
  // Updates sections when propSections is provided (including empty arrays)
  React.useEffect(() => {
    if (propSections !== undefined) {
      setSections(propSections);
    }
  }, [propSections, isGroup, isAllowDnD]);

  // Filter state for search functionality
  const [filterText, setFilterText] = React.useState<string>('');

  // Filter function to filter sections based on search text
  const filterSections = React.useCallback((sections: IColumnSection[], filterText: string): IColumnSection[] => {
    if (!filterText || !filterText.trim()) {
      return sections;
    }

    const lowerFilterText = filterText.toLowerCase().trim();
    
    return sections
      .map(section => {
        const filteredItems = (section.items || []).filter(item =>
          item.label.toLowerCase().includes(lowerFilterText)
        );
        
        if (filteredItems.length === 0) {
          return null;
        }
        
        return {
          ...section,
          items: filteredItems,
        };
      })
      .filter((section): section is IColumnSection => section !== null);
  }, []);

  // Filtered sections based on filter text
  const filteredSections = useMemo(() => {
    return filterSections(sections, filterText);
  }, [sections, filterText, filterSections]);

  // Flatten all items from sections for lookup
  const allItemsMap = React.useMemo(() => {
    const map = new Map<string, IColumnItem>();
    sections.forEach((section) => {
      section.items?.forEach((item) => {
        map.set(item.id, item);
      });
    });
    return map;
  }, [sections]);

  // Track multiple selected keys
  const [selectedKeys, setSelectedKeys] = React.useState<Set<string>>(new Set<string>());

  // Calculate selection state: all selected, none selected, or mixed
  const selectionState = useMemo(() => {
    const totalItems = allItemsMap.size;
    const selectedCount = selectedKeys.size;
    
    if (totalItems === 0) {
      return 'none';
    }
    
    if (selectedCount === 0) {
      return 'none';
    }
    
    if (selectedCount === totalItems) {
      return 'all';
    }
    
    return 'mixed';
  }, [selectedKeys, allItemsMap]);
  const previousSelectedKeysRef = React.useRef<Set<string>>(new Set<string>());
  // Counter to force remount when Select All is clicked
  const [selectAllCounter, setSelectAllCounter] = React.useState<number>(0);

  const handleSelectionChange = React.useCallback((keys: Set<string> | 'all') => {
    const newSelectedKeys = keys === 'all' 
      ? new Set<string>(Array.from(allItemsMap.keys()))
      : keys;

      setSelectedKeys(newSelectedKeys);

    if (onItemSelectionChanged) {
      // Determine which items were added or removed
      const previous = previousSelectedKeysRef.current;
      
      // Items that were added (selected)
      newSelectedKeys.forEach((key) => {
        if (!previous.has(key)) {
          const item = allItemsMap.get(key);
          if (item) {
            onItemSelectionChanged(true, item);
          }
        }
      });

      // Items that were removed (deselected)
      previous.forEach((key) => {
        if (!newSelectedKeys.has(key)) {
          const item = allItemsMap.get(key);
          if (item) {
            onItemSelectionChanged(false, item);
          }
        }
      });
    }

    previousSelectedKeysRef.current = new Set(newSelectedKeys);
  }, [allItemsMap, onItemSelectionChanged]);

  // Handle visibility change - update state and call callback if provided
  const handleVisibilityChange = React.useCallback((itemId: string, isVisible: boolean) => {
    setSections((prevSections) => {
      return prevSections.map((section) => ({
        ...section,
        items: section.items?.map((item) =>
          item.id === itemId ? { ...item, isVisible } : item
        ),
      }));
    });

    // Call the external callback if provided
    if (onVisibilityChange) {
      onVisibilityChange(itemId, isVisible);
    }
  }, [onVisibilityChange]);

  // Handle filter change - update state and call callback if provided
  const handleFilterChange = React.useCallback((itemId: string, isFilter: boolean) => {
    setSections((prevSections) => {
      return prevSections.map((section) => ({
        ...section,
        items: section.items?.map((item) =>
          item.id === itemId ? { ...item, isFilter } : item
        ),
      }));
    });

    // Call the external callback if provided
    if (onFilterChange) {
      onFilterChange(itemId, isFilter);
    }
  }, [onFilterChange]);

  // Handle Select All / Unselect All - toggle between select all and unselect all
  const handleSelectAll = React.useCallback(() => {
    // Increment counter to force remount
    setSelectAllCounter(prev => prev + 1);
    
    if (selectionState === 'all') {
      // If all are selected, deselect all
      handleSelectionChange(new Set<string>());
    } else {
      // Otherwise, select all (covers both 'none' and 'mixed' states)
      const allItemIds = new Set<string>(Array.from(allItemsMap.keys()));
      handleSelectionChange(allItemIds);
    }
  }, [allItemsMap, handleSelectionChange, selectionState]);

  // Expose methods to get selected items and clear selection
  useImperativeHandle(forwardedRef, () => ({
    getSelectedItems: () => Array.from(selectedKeys),
    clearSelection: () => {
      setSelectedKeys(new Set<string>());
      previousSelectedKeysRef.current = new Set<string>();
    }
  }), [selectedKeys]);

  return (
    <Flex direction='column' width={width} UNSAFE_className={className}>
      <Flex direction='row' gap={'size-10'} justifyContent={'space-between'} UNSAFE_className={classNameHeader}>
        <View alignSelf={'center'} paddingStart={8}>
          <Text UNSAFE_className={styles.text_field}>{headerTitle}</Text>
        </View>
        <View>
          <AtollonButton 
            onPress={() => {
              handleSelectAll();
              if (onSelectAllPress) {
                onSelectAllPress();
              }
            }} 
            designType={selectAllButtonDesignType}
          >
            {selectionState === 'all' ? 'Unselect All' : selectAllText}
          </AtollonButton>
        </View>
      </Flex>
      <Flex direction="row" gap="size-50" alignItems="center" width="100%" UNSAFE_className={classNameBody} UNSAFE_style={{ padding: '3px 20px 0px 8px' }}>
          <TextField
            width="100%"
            value={filterText}
            onChange={setFilterText}
            description="Search columns..."
            aria-label={`Search ${headerTitle.toLowerCase()}`}
            UNSAFE_className={classNameSearchField}
          />
        {filterText && (
          <ActionButton
            onPress={() => setFilterText('')}
            aria-label="Clear search"
          >
            <Close />
          </ActionButton>
        )}
      </Flex>
      <View height={height} UNSAFE_className={classNameBody}>
        {(() => {
          return (
            <ColumnsList
              key={`cols-${
                selectedKeys instanceof Set
                  ? Array.from(selectedKeys).sort().join(',')
                  : 'none'
              }-${selectAllCounter}`}
              sections={filteredSections}
              selectedKeys={selectedKeys}
              onSelectionChange={handleSelectionChange}
              onVisibilityChange={handleVisibilityChange}
              onFilterChange={handleFilterChange}
              onReorder={onReOrder}
              isGroup={isGroup}
              isAllowDnD={isAllowDnD}
            />
          );
        })()}
      </View>
    </Flex>
  );
});

ColumnsSelector.displayName = 'ColumnsSelector';
